import { useState, useEffect } from 'react';
import ContactForm from './components/ContactForm';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      const sections = ['home', 'about', 'services', 'references', 'gallery', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMenuOpen(false);
  };

  const navItems = [
    { id: 'home', label: 'Ana Sayfa' },
    { id: 'about', label: 'Hakkımızda' },
    { id: 'services', label: 'Hizmetlerimiz' },
    { id: 'references', label: 'Referanslar' },
    { id: 'gallery', label: 'Galeri' },
    { id: 'contact', label: 'İletişim' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || menuOpen ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="w-full px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <img
              src="https://static.readdy.ai/image/73e643dee18fcd4ce852476cc145d102/682bb3d92ca2d1ae5cb9771a636fa40d.jpeg"
              alt="Serdar Marin Mekanik Logo"
              className="h-10 w-10 md:h-12 md:w-12 object-contain"
            />
            <span className={`text-base md:text-xl font-bold transition-colors ${
              isScrolled || menuOpen ? 'text-[#1A2332]' : 'text-white'
            }`}>Serdar Marin Mekanik</span>
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm font-medium transition-all relative group whitespace-nowrap cursor-pointer ${
                  isScrolled
                  ? 'text-[#1A2332] hover:text-[#00BCD4]'
                  : 'text-white hover:text-[#00BCD4]'
                }`}
              >
                {item.label}
                <span className={`absolute bottom-0 left-0 w-0 h-0.5 bg-[#00BCD4] transition-all group-hover:w-full ${
                  activeSection === item.id ? 'w-full' : ''
                }`}></span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => scrollToSection('contact')}
              className={`hidden lg:block px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? 'bg-[#00BCD4] text-white hover:bg-[#00ACC1]'
                  : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
              }`}
            >
              Teklif Alın
            </button>

            {/* Hamburger */}
            <button
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                isScrolled || menuOpen ? 'text-[#1A2332]' : 'text-white'
              }`}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 px-4 py-4 flex flex-col gap-1">
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-left px-4 py-3 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                  activeSection === item.id
                    ? 'bg-[#00BCD4]/10 text-[#00BCD4]'
                    : 'text-[#1A2332] hover:bg-gray-50'
                }`}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => scrollToSection('contact')}
              className="mt-2 px-4 py-3 bg-[#00BCD4] text-white rounded-lg font-medium text-sm whitespace-nowrap cursor-pointer"
            >
              Teklif Alın
            </button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/4d37f315b393aa65b46ec56360974493.jpg"
            alt="Marine Engine Service"
            className="w-full h-full object-cover object-center"
          />
        </div>

        <div className="relative z-10 text-center px-4 md:px-6 w-full max-w-5xl mx-auto py-32">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 leading-tight">
            PROFESYONELİZMİN ADRESİ<br />
            DENİZ MOTORU UZMANLARI
          </h1>
          <p className="text-base md:text-xl text-white/90 mb-10 font-light max-w-3xl mx-auto">
            2017'den beri mega yatlar ve teknelere özel motor bakım, onarım ve revizyon hizmetleri
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => scrollToSection('services')}
              className="w-full sm:w-auto px-8 py-4 bg-white/20 backdrop-blur-sm text-white rounded-lg font-medium hover:bg-white/30 transition-all whitespace-nowrap cursor-pointer"
            >
              Hizmetlerimiz
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="w-full sm:w-auto px-8 py-4 bg-[#00BCD4] text-white rounded-lg font-medium hover:bg-[#00ACC1] transition-all whitespace-nowrap cursor-pointer"
            >
              Bize Ulaşın
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <i className="ri-anchor-line text-[#00BCD4] text-2xl"></i>
            <span className="text-[#00BCD4] font-semibold text-sm uppercase tracking-wider">Hakkımızda</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-[#1A2332] mb-8">
            Deneyim ve Güvenin Buluşma Noktası
          </h2>
          <p className="text-base md:text-lg text-[#2C3E50] leading-relaxed mb-12">
            2017'den beri kurmuş olduğumuz firmamızla yatların mekanik motor ve güç aktarma organlarına hizmet vermekteyiz. Tecrübeli ve uzman kadromuz ile her geçen yıl müşteri portföyümüzü büyüterek ilerliyoruz. Mega yatlar, yelkenli tekneler ve 24 metre üzeri guletler müşteri ağırlıklı olarak memnuniyet referansları ile büyüyoruz. ⚓
          </p>
          <div className="grid grid-cols-3 gap-4 md:gap-8 mt-8 md:mt-16">
            {[
              { number: '7+', label: 'Yıllık Deneyim' },
              { number: '100+', label: 'Mutlu Müşteri' },
              { number: '500+', label: 'Tamamlanan Proje' }
            ].map((stat, index) => (
              <div key={index} className="bg-white rounded-2xl p-4 md:p-8 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-3xl md:text-5xl font-bold text-[#00BCD4] mb-2">{stat.number}</div>
                <div className="text-xs md:text-base text-[#2C3E50] font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <span className="text-[#00BCD4] font-semibold text-sm uppercase tracking-wider">Hizmetlerimiz</span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#1A2332] mt-4">
              Kapsamlı Deniz Motoru Çözümleri
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                title: 'Motor Bakım & Onarım',
                description: 'Dizel yat motorları için profesyonel servis',
                image: 'https://readdy.ai/api/search-image?query=yacht%20diesel%20engine%20maintenance%20professional%20mechanic%20working%20detailed%20engine%20components%20turquoise%20blue%20lighting%20clean%20workshop%20environment%20technical%20precision%20maritime%20service&width=600&height=700&seq=service-engine-1&orientation=portrait',
                services: ['Dizel motor revizyonu', 'Periyodik bakım', 'Arıza tespiti', 'Performans optimizasyonu']
              },
              {
                title: 'Jeneratör Servisi',
                description: 'Deniz jeneratörleri bakım ve onarım',
                image: 'https://readdy.ai/api/search-image?query=marine%20generator%20service%20maintenance%20professional%20technician%20yacht%20power%20system%20turquoise%20lighting%20clean%20technical%20environment%20electrical%20components%20maritime%20equipment&width=600&height=700&seq=service-generator-1&orientation=portrait',
                services: ['Jeneratör revizyonu', 'Elektrik sistemi kontrolü', 'Yedek parça değişimi', 'Performans testleri']
              },
              {
                title: 'Şaft & Pervane Sistemleri',
                description: 'Güç aktarma organları uzman servisi',
                image: 'https://readdy.ai/api/search-image?query=yacht%20propeller%20shaft%20system%20maintenance%20professional%20marine%20mechanic%20underwater%20components%20turquoise%20water%20technical%20precision%20maritime%20service%20detailed%20mechanical%20parts&width=600&height=700&seq=service-shaft-1&orientation=portrait',
                services: ['Şaft balans ayarı', 'Pervane revizyonu', 'Yatak değişimi', 'Survey raporlaması']
              }
            ].map((service, index) => (
              <div key={index} className="group rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                <div className="relative h-64 md:h-80 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black/70"></div>
                  <div className="absolute inset-0 flex flex-col justify-end p-6">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-2">{service.title}</h3>
                    <p className="text-white/90 text-sm font-light">{service.description}</p>
                  </div>
                </div>
                <div className="bg-white p-6">
                  <ul className="space-y-3">
                    {service.services.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-[#2C3E50]">
                        <i className="ri-check-line text-[#00BCD4] text-lg mt-0.5 flex-shrink-0"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Services */}
          <div className="mt-12 md:mt-16 bg-[#F8F9FA] rounded-2xl p-6 md:p-12">
            <h3 className="text-2xl md:text-3xl font-bold text-[#1A2332] mb-6 md:mb-8 text-center">Diğer Hizmetlerimiz</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {[
                'Tüm deniz şanzımanları bakım onarım',
                'Hidrolik ve elektrikli ırgatlar, vinçler bakım onarım ve sıfır montaj',
                'Baş ve arka traster motorları bakım onarım, montaj',
                'Hidrolik sistemler, arka platformları, flapların bakım onarımı'
              ].map((service, index) => (
                <div key={index} className="flex items-start gap-4 bg-white p-5 md:p-6 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#00BCD4]/10 rounded-lg flex-shrink-0">
                    <i className="ri-tools-line text-[#00BCD4] text-xl"></i>
                  </div>
                  <p className="text-[#2C3E50] font-medium text-sm md:text-base">{service}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* References Section */}
      <section id="references" className="py-16 md:py-24 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <i className="ri-ship-line text-[#00BCD4] text-2xl"></i>
              <span className="text-[#00BCD4] font-semibold text-sm uppercase tracking-wider">Referanslarımız</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-[#1A2332]">
              Müşterilerimizin Güveni, Bizim Gururumuz
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {[
              { name: 'Mustafa Bey', company: 'Pilatin Marin İstanbul', role: 'Yat İşletme Müdürü' },
              { name: 'Ziya Aksoy', company: 'Yacht Broker', role: 'Broker' },
              { name: 'Cihat Bey', company: 'Ghost Cadde Makina A.Ş.', role: 'Genel Müdür' }
            ].map((ref, index) => (
              <div key={index} className="bg-[#E8F4F8] rounded-2xl p-6 md:p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#00BCD4] rounded-full text-white text-xl font-bold flex-shrink-0">
                    {ref.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-bold text-[#1A2332]">{ref.name}</div>
                    <div className="text-sm text-[#2C3E50]/70">{ref.role}</div>
                  </div>
                </div>
                <i className="ri-double-quotes-l text-5xl text-[#00BCD4]/30 mb-4 block"></i>
                <p className="text-[#1A2332] leading-relaxed">{ref.company}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <div className="md:col-span-2 relative rounded-2xl overflow-hidden h-56 md:h-64 group">
              <img
                src="https://readdy.ai/api/search-image?query=luxury%20yacht%20marina%20service%20professional%20marine%20mechanics%20working%20on%20mega%20yacht%20engine%20turquoise%20water%20background%20high-end%20maritime%20service%20panoramic%20view&width=1200&height=500&seq=ref-panoramic-1&orientation=landscape"
                alt="Reference"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A2332]/90 via-[#1A2332]/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-6 md:p-8 text-white">
                <div className="font-bold text-xl mb-1">Selim Yeşil</div>
                <div className="text-white/80 mb-2">Serenity Azimut 86</div>
                <p className="text-sm text-white/90 italic">"Motor revizyonumuz kusursuz tamamlandı, profesyonel hizmet"</p>
              </div>
            </div>

            <div className="flex flex-col gap-4 md:gap-6">
              {[
                { name: 'Hakan Uğuz', company: 'Youtori Sunseeker 95' },
                { name: 'Ahmet Kaptan', company: 'Casa Costa Bodrum' }
              ].map((ref, index) => (
                <div key={index} className="bg-[#E8F4F8] rounded-2xl p-5 md:p-6 flex-1 hover:shadow-lg transition-shadow">
                  <i className="ri-double-quotes-l text-3xl text-[#00BCD4]/30 mb-3 block"></i>
                  <p className="text-[#1A2332] mb-4 text-sm leading-relaxed">{ref.company}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#00BCD4] rounded-full text-white text-sm font-bold flex-shrink-0">
                      {ref.name.charAt(0)}
                    </div>
                    <div className="font-bold text-[#1A2332] text-sm">{ref.name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* More References */}
          <div className="mt-8 md:mt-12 bg-white rounded-2xl p-6 md:p-8">
            <h3 className="text-xl md:text-2xl font-bold text-[#1A2332] mb-6 text-center">Diğer Referanslarımız</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {[
                'Endlens Dortek Kapı İstanbul',
                'Kutlu Yat İstanbul - Ramazan Bey',
                'May Way Hanse 575 - Aykut Bey',
                'Seven Yalıkavak - Özben Kaptan',
                'Sonadora Yalıkavak - Bayram Kaptan',
                'Bodrum De Yat Charter - Aykut ve Erhan Bey',
                'Bodrum Tour Queen - Kaan Bey',
                'Mist Moder İstanbul - İlker Bey',
                'Oko Akfen Gayrimenkul - Hüseyin Kaptan',
                'Emperyo - Ari Bey',
                'Monte Trusto - Stefan Bey',
                'Ören T Marin - Aynur Hanım',
                'Advokat Katamaran - Andrea Bey',
                'Kepez 2 - Mehmet Kaptan',
                'Angel Adik Yeşil - Dursun Kaptan',
                'Sancar - Muhammet Kaptan',
                'Aicon 72 - Taner Kaptan',
                'Nice Muu Yamata Holding - Eyüp Bey',
                'Egean Kliper - Ergün Malatyalı',
                'Kırmızı Yeşil - Cengiz Bey',
                'Next Sea - Ceyhun Bey Mersin',
                'Arda Denizcilik - Mesut Bey'
              ].map((ref, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-[#F8F9FA] rounded-lg hover:bg-[#E8F4F8] transition-colors">
                  <i className="ri-checkbox-circle-fill text-[#00BCD4] text-lg flex-shrink-0"></i>
                  <span className="text-sm text-[#2C3E50]">{ref}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <i className="ri-image-line text-[#00BCD4] text-2xl"></i>
              <span className="text-[#00BCD4] font-semibold text-sm uppercase tracking-wider">Galeri</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-[#1A2332]">
              Çalışmalarımızdan Kareler
            </h2>
            <p className="text-base md:text-lg text-[#2C3E50] mt-4 max-w-2xl mx-auto">
              Gerçekleştirdiğimiz motor bakım, onarım ve revizyon çalışmalarından fotoğraflar
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Gerçek fotoğraf 1 - John Deere motor revizyonu */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer col-span-1 sm:col-span-2 lg:col-span-2">
              <div className="w-full h-72 md:h-96">
                <img
                  src="https://static.readdy.ai/image/73e643dee18fcd4ce852476cc145d102/f9fb664903d11e6b20501ee98e6de113.jpeg"
                  alt="John Deere Motor Revizyonu - Serdar Marin Mekanik"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 p-5 text-white translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="text-sm font-semibold bg-[#00BCD4] px-3 py-1 rounded-full">Motor Revizyonu</span>
                  <p className="mt-2 text-sm text-white/90">John Deere Deniz Motoru - Silindir Kapağı Revizyonu</p>
                </div>
              </div>
            </div>

            {/* Gerçek fotoğraf 2 - Kubota motor servisi */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer">
              <div className="w-full h-72 md:h-96">
                <img
                  src="https://static.readdy.ai/image/73e643dee18fcd4ce852476cc145d102/f794da51d3255c9b55ee42c5ad426e60.jpeg"
                  alt="Kubota Deniz Motoru Servisi - Serdar Marin Mekanik"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 p-5 text-white translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="text-sm font-semibold bg-[#00BCD4] px-3 py-1 rounded-full">Motor Servisi</span>
                  <p className="mt-2 text-sm text-white/90">Kubota Deniz Motoru - Profesyonel Bakım & Onarım</p>
                </div>
              </div>
            </div>

            {/* Gerçek fotoğraf 3 - Yanmar motor servisi */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer">
              <div className="w-full h-72 md:h-96">
                <img
                  src="https://static.readdy.ai/image/73e643dee18fcd4ce852476cc145d102/e62c479a7f069ce5dc025343925a0fcd.jpeg"
                  alt="Yanmar Deniz Motoru Servisi - Serdar Marin Mekanik"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 p-5 text-white translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="text-sm font-semibold bg-[#00BCD4] px-3 py-1 rounded-full">Yanmar Motor</span>
                  <p className="mt-2 text-sm text-white/90">Yanmar Deniz Motoru - Bakım &amp; Onarım</p>
                </div>
              </div>
            </div>

            {/* Gerçek fotoğraf 4 - Motor bloğu ve krank mili revizyonu */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer col-span-1 sm:col-span-2 lg:col-span-2">
              <div className="w-full h-72 md:h-96">
                <img
                  src="https://static.readdy.ai/image/73e643dee18fcd4ce852476cc145d102/99c6fe95343d0cf6a0f93f31d35b74a7.jpeg"
                  alt="Motor Bloğu ve Krank Mili Revizyonu - Serdar Marin Mekanik"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 p-5 text-white translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="text-sm font-semibold bg-[#00BCD4] px-3 py-1 rounded-full">Motor Revizyonu</span>
                  <p className="mt-2 text-sm text-white/90">Motor Bloğu &amp; Krank Mili - Komple Revizyon</p>
                </div>
              </div>
            </div>

            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer">
              <div className="w-full h-64 md:h-72">
                <img
                  src="https://readdy.ai/api/search-image?query=marine%20diesel%20engine%20cylinder%20head%20overhaul%20professional%20mechanic%20hands%20working%20on%20engine%20block%20clean%20workshop%20technical%20precision%20maritime%20service%20close%20up%20detailed%20mechanical%20components&width=600&height=500&seq=gallery-engine-2&orientation=landscape"
                  alt="Motor Bakım Çalışması"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 p-5 text-white translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="text-sm font-semibold bg-[#00BCD4] px-3 py-1 rounded-full">Motor Bakım</span>
                  <p className="mt-2 text-sm text-white/90">Dizel Motor Periyodik Bakım</p>
                </div>
              </div>
            </div>

            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer">
              <div className="w-full h-64 md:h-72">
                <img
                  src="https://readdy.ai/api/search-image?query=yacht%20generator%20service%20maintenance%20professional%20technician%20marine%20power%20system%20engine%20room%20technical%20work%20close%20up%20detailed%20components%20maritime%20equipment%20repair&width=600&height=500&seq=gallery-generator-3&orientation=landscape"
                  alt="Jeneratör Servisi"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 p-5 text-white translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="text-sm font-semibold bg-[#00BCD4] px-3 py-1 rounded-full">Jeneratör</span>
                  <p className="mt-2 text-sm text-white/90">Deniz Jeneratörü Bakım & Onarım</p>
                </div>
              </div>
            </div>

            {/* Gerçek fotoğraf 5 - Volvo Penta motor servisi */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer col-span-1 sm:col-span-2 lg:col-span-2">
              <div className="w-full h-72 md:h-96">
                <img
                  src="https://static.readdy.ai/image/73e643dee18fcd4ce852476cc145d102/1ec25aefcc62a333cce7883d4ef4b72b.jpeg"
                  alt="Volvo Penta Deniz Motoru Servisi - Serdar Marin Mekanik"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 p-5 text-white translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="text-sm font-semibold bg-[#00BCD4] px-3 py-1 rounded-full">Volvo Penta</span>
                  <p className="mt-2 text-sm text-white/90">Volvo Penta Deniz Motoru - Bakım &amp; Onarım</p>
                </div>
              </div>
            </div>

            {/* Gerçek fotoğraf 6 - Volvo Penta komple revizyon */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer">
              <div className="w-full h-72 md:h-96">
                <img
                  src="https://static.readdy.ai/image/73e643dee18fcd4ce852476cc145d102/98cc5e7beb548800f2df6577be182d98.jpeg"
                  alt="Volvo Penta Komple Motor Revizyonu - Serdar Marin Mekanik"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 p-5 text-white translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="text-sm font-semibold bg-[#00BCD4] px-3 py-1 rounded-full">Volvo Penta</span>
                  <p className="mt-2 text-sm text-white/90">Volvo Penta - Komple Motor Revizyonu &amp; Boyama</p>
                </div>
              </div>
            </div>

            {/* Gerçek fotoğraf 7 - Saha servisi, marinada motor taşıma */}
            <div className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer col-span-1 sm:col-span-2 lg:col-span-2">
              <div className="w-full h-72 md:h-96">
                <img
                  src="https://static.readdy.ai/image/73e643dee18fcd4ce852476cc145d102/9b51350a414967130faf88f70d47a182.jpeg"
                  alt="Saha Servisi - Marinada Motor Taşıma ve Montaj - Serdar Marin Mekanik"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 p-5 text-white translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="text-sm font-semibold bg-[#00BCD4] px-3 py-1 rounded-full">Saha Servisi</span>
                  <p className="mt-2 text-sm text-white/90">Marinada Motor Taşıma &amp; Saha Montaj Hizmeti</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <p className="text-[#2C3E50]/70 text-sm">
              Daha fazla çalışma görmek için{' '}
              <a
                href="https://wa.me/905425961246"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00BCD4] font-semibold hover:underline cursor-pointer"
              >
                WhatsApp'tan bize ulaşın
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative min-h-[50vh] md:h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=luxury%20marina%20yacht%20harbor%20panoramic%20view%20turquoise%20water%20professional%20maritime%20service%20sunset%20golden%20hour%20wide%20angle%20beautiful%20seascape&width=1920&height=1080&seq=cta-marina-1&orientation=landscape"
            alt="Marina"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-0">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 md:gap-0">
            <div>
              <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight">
                PROFESYONEL SERVİS<br />
                HİZMETİNİZDE
              </h2>
            </div>
            <div className="text-left md:text-right">
              <p className="text-lg md:text-xl text-white font-light mb-2">Belgeli ve tecrübeli kadromuzla</p>
              <p className="text-lg md:text-xl text-white font-light">7/24 hizmet desteği</p>
            </div>
          </div>

          <div className="mt-8 md:mt-12">
            <button
              onClick={() => scrollToSection('contact')}
              className="group flex items-center gap-4 bg-white rounded-full pl-8 pr-2 py-2 hover:scale-105 transition-transform cursor-pointer whitespace-nowrap"
            >
              <span className="text-[#1A2332] font-medium">İletişime Geçin</span>
              <div className="w-12 h-12 flex items-center justify-center bg-[#1A2332] rounded-full">
                <i className="ri-arrow-right-line text-white text-xl"></i>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <span className="text-[#00BCD4] font-semibold text-sm uppercase tracking-wider">İletişim</span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#1A2332] mt-4 mb-4">
              Bizimle İletişime Geçin
            </h2>
            <p className="text-base md:text-lg text-[#2C3E50]">Profesyonel deniz motoru hizmetleri için bize ulaşın</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-8 mb-10 md:mb-16">
            <div className="bg-[#F8F9FA] rounded-2xl p-6 md:p-8 text-center hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center bg-[#00BCD4] rounded-full mx-auto mb-5">
                <i className="ri-phone-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-[#1A2332] mb-3">Telefon</h3>
              <a href="tel:+905425961246" className="text-[#2C3E50] hover:text-[#00BCD4] transition-colors block mb-2 text-sm md:text-base">
                0542 596 12 46
              </a>
              <p className="text-xs md:text-sm text-[#2C3E50]/70 mb-2">Ofis / Muhasebe</p>
              <a href="tel:+905306691224" className="text-[#2C3E50] hover:text-[#00BCD4] transition-colors block text-sm md:text-base">
                0530 669 12 24
              </a>
            </div>

            <div className="bg-[#F8F9FA] rounded-2xl p-6 md:p-8 text-center hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center bg-[#00BCD4] rounded-full mx-auto mb-5">
                <i className="ri-whatsapp-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-[#1A2332] mb-3">WhatsApp Destek</h3>
              <a href="https://wa.me/905425961246" target="_blank" rel="noopener noreferrer" className="text-[#2C3E50] hover:text-[#00BCD4] transition-colors block text-sm md:text-base">
                0542 596 12 46
              </a>
              <p className="text-xs md:text-sm text-[#2C3E50]/70 mt-2">7/24 Destek Hattı</p>
            </div>

            <div className="bg-[#F8F9FA] rounded-2xl p-6 md:p-8 text-center hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center bg-[#00BCD4] rounded-full mx-auto mb-5">
                <i className="ri-mail-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-[#1A2332] mb-3">E-posta</h3>
              <a href="mailto:serdarmarinmekanik@gmail.com" className="text-[#2C3E50] hover:text-[#00BCD4] transition-colors break-all text-sm md:text-base">
                serdarmarinmekanik@gmail.com
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
            {/* Left: Image + Info */}
            <div className="relative rounded-2xl overflow-hidden h-72 md:h-auto md:min-h-[520px]">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20marine%20mechanic%20team%20working%20on%20luxury%20yacht%20engine%20room%20turquoise%20sea%20background%20clean%20workshop%20environment%20expert%20technicians%20maritime%20service%20high%20quality%20photography&width=800&height=900&seq=contact-form-img-1&orientation=portrait"
                alt="Serdar Marin Mekanik Ekibi"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A2332]/90 via-[#1A2332]/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-6 md:p-8 text-white">
                <h3 className="text-xl md:text-2xl font-bold mb-3">Neden Bizi Seçmelisiniz?</h3>
                <ul className="space-y-2 md:space-y-3">
                  {[
                    '7+ yıllık sektör deneyimi',
                    'Belgeli ve uzman teknik kadro',
                    'Mega yat ve gulet uzmanlığı',
                    'Hızlı ve güvenilir servis',
                    '100+ memnun müşteri referansı'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-white/90">
                      <i className="ri-check-double-line text-[#00BCD4] text-lg flex-shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right: Form */}
            <div className="bg-[#F8F9FA] rounded-2xl p-6 md:p-10">
              <h3 className="text-xl md:text-2xl font-bold text-[#1A2332] mb-2">Mesaj & Randevu Talebi</h3>
              <p className="text-[#2C3E50]/70 text-sm mb-6 md:mb-8">Formu doldurun, en kısa sürede size dönelim.</p>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A2332] text-white pt-12 md:pt-20 pb-8 md:pb-12">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16 mb-12 md:mb-20">
            <div>
              <h4 className="text-base md:text-lg font-bold mb-4">Hizmetler</h4>
              <div className="w-10 h-0.5 bg-[#00BCD4] mb-4 md:mb-6"></div>
              <ul className="space-y-2 md:space-y-3">
                <li><button onClick={() => scrollToSection('services')} className="text-[#B0BEC5] hover:text-white transition-colors cursor-pointer text-sm">Motor Bakım</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-[#B0BEC5] hover:text-white transition-colors cursor-pointer text-sm">Jeneratör Servisi</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-[#B0BEC5] hover:text-white transition-colors cursor-pointer text-sm">Şaft Sistemleri</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-[#B0BEC5] hover:text-white transition-colors cursor-pointer text-sm">Hidrolik Sistemler</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-base md:text-lg font-bold mb-4">Kurumsal</h4>
              <div className="w-10 h-0.5 bg-[#00BCD4] mb-4 md:mb-6"></div>
              <ul className="space-y-2 md:space-y-3">
                <li><button onClick={() => scrollToSection('about')} className="text-[#B0BEC5] hover:text-white transition-colors cursor-pointer text-sm">Hakkımızda</button></li>
                <li><button onClick={() => scrollToSection('references')} className="text-[#B0BEC5] hover:text-white transition-colors cursor-pointer text-sm">Referanslar</button></li>
                <li><button onClick={() => scrollToSection('gallery')} className="text-[#B0BEC5] hover:text-white transition-colors cursor-pointer text-sm">Galeri</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-[#B0BEC5] hover:text-white transition-colors cursor-pointer text-sm">İletişim</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-base md:text-lg font-bold mb-4">Referanslar</h4>
              <div className="w-10 h-0.5 bg-[#00BCD4] mb-4 md:mb-6"></div>
              <ul className="space-y-2 md:space-y-3">
                <li className="text-[#B0BEC5] text-sm">Pilatin Marin</li>
                <li className="text-[#B0BEC5] text-sm">Ziya Aksoy Yacht</li>
                <li className="text-[#B0BEC5] text-sm">Ghost Cadde Makina</li>
                <li className="text-[#B0BEC5] text-sm">Serenity Azimut 86</li>
              </ul>
            </div>

            <div>
              <h4 className="text-base md:text-lg font-bold mb-4">İletişim</h4>
              <div className="w-10 h-0.5 bg-[#00BCD4] mb-4 md:mb-6"></div>
              <ul className="space-y-2 md:space-y-3">
                <li className="text-[#B0BEC5] text-sm">0542 596 12 46</li>
                <li className="text-[#B0BEC5] text-sm">0530 669 12 24</li>
                <li className="text-[#B0BEC5] break-all text-sm">serdarmarinmekanik@gmail.com</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 md:pt-12">
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 md:gap-0">
              <div>
                <div className="text-4xl md:text-7xl lg:text-8xl font-black mb-2">SERDAR MARİN</div>
                <div className="text-xl md:text-3xl lg:text-4xl font-bold text-[#B0BEC5]">MEKANİK</div>
                <div className="flex items-center gap-3 mt-4 md:mt-6">
                  <div className="w-12 h-0.5 bg-[#00BCD4]"></div>
                  <span className="text-xs md:text-sm text-[#B0BEC5]">Profesyonel Deniz Motoru Hizmetleri</span>
                </div>
              </div>

              <div className="text-left md:text-right">
                <div className="text-xs md:text-sm text-[#B0BEC5] mb-4">© 2024 Serdar Marin Mekanik. Tüm hakları saklıdır.</div>
                <div className="flex items-center gap-3 md:justify-end">
                  <a href="https://wa.me/905425961246" target="_blank" rel="noopener noreferrer" className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center border-2 border-white/30 rounded-full hover:bg-[#00BCD4] hover:border-[#00BCD4] transition-all cursor-pointer">
                    <i className="ri-whatsapp-line text-2xl"></i>
                  </a>
                  <a href="tel:+905425961246" className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center border-2 border-white/30 rounded-full hover:bg-[#00BCD4] hover:border-[#00BCD4] transition-all cursor-pointer">
                    <i className="ri-phone-line text-2xl"></i>
                  </a>
                  <a href="mailto:serdarmarinmekanik@gmail.com" className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center border-2 border-white/30 rounded-full hover:bg-[#00BCD4] hover:border-[#00BCD4] transition-all cursor-pointer">
                    <i className="ri-mail-line text-2xl"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Fixed WhatsApp Button */}
      <a
        href="https://wa.me/905425961246"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-24 right-5 z-50 flex items-center gap-2 bg-[#25D366] text-white pl-4 pr-5 py-3 rounded-full shadow-lg hover:bg-[#1ebe5d] hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
        aria-label="WhatsApp ile iletişime geçin"
      >
        <div className="w-6 h-6 flex items-center justify-center">
          <i className="ri-whatsapp-line text-2xl"></i>
        </div>
        <span className="text-sm font-semibold">WhatsApp</span>
      </a>
    </div>
  );
}
